import { el, clear } from '../dom.js';
import { t } from '../i18n.js';

// Upload sends the file as a raw body rather than multipart: parsing
// multipart on the server would mean a fifth runtime dependency for one
// endpoint, and fetch(file) sends the bytes with the right Content-Type
// natively.
// `seriesId` may be null: on the wizard's first slide the flyer is picked
// before the record exists. The files are held and sent the moment the draft
// is created, which is also why the wizard saves on leaving that slide — the
// slowest step in the flow starts while the publisher is still typing, and
// nothing about it can block an event that needs no artwork.
export function imageField({ seriesId = null, csrfHeader }) {
  let series = seriesId;
  let queued = [];

  const list = el('ul', { class: 'img-list' });
  const status = el('p', { class: 'card-meta' });
  status.hidden = true;

  const picker = el('input', { type: 'file', accept: 'image/jpeg,image/png,image/webp,image/avif', multiple: true });
  picker.hidden = true;

  const drop = el('button', { class: 'img-drop', type: 'button' }, t.dropImages);
  drop.addEventListener('click', () => picker.click());
  picker.addEventListener('change', () => upload([...picker.files]));

  for (const name of ['dragenter', 'dragover']) {
    drop.addEventListener(name, (event) => {
      event.preventDefault();
      drop.classList.add('img-drop-over');
    });
  }
  for (const name of ['dragleave', 'drop']) {
    drop.addEventListener(name, (event) => {
      event.preventDefault();
      drop.classList.remove('img-drop-over');
    });
  }
  drop.addEventListener('drop', (event) => {
    upload([...(event.dataTransfer?.files ?? [])].filter((file) => file.type.startsWith('image/')));
  });

  const root = el('div', { class: 'field' }, [
    el('span', { class: 'rec-label' }, t.artwork),
    list,
    drop,
    picker,
    status,
    el('p', { class: 'card-meta' }, t.rotationHint),
    el('p', { class: 'card-meta' }, t.imagesStripped),
  ]);

  if (series) refresh();

  async function refresh() {
    if (!series) return;
    const images = await request('GET', `/panel/api/series/${series}/images`);
    clear(list);
    if (!Array.isArray(images)) return;
    for (const image of images) list.append(item(image));
  }

  function item(image) {
    const remove = el('button', { class: 'img-remove', type: 'button', 'aria-label': t.removeImage }, '×');
    remove.addEventListener('click', async () => {
      remove.disabled = true;
      await request('DELETE', `/panel/api/images/${image.id}`);
      await refresh();
    });

    // A flyer carries the line-up, the address and the times. Without alt text
    // none of that reaches a screen reader, and the column has existed since
    // the first migration with nothing able to write it.
    const alt = el('input', {
      type: 'text',
      class: 'img-alt',
      value: image.alt ?? '',
      placeholder: t.altPlaceholder,
      'aria-label': t.altLabel,
      maxlength: '300',
    });

    let saved = image.alt ?? '';
    alt.addEventListener('change', async () => {
      if (alt.value === saved) return;
      saved = alt.value;
      await request('PATCH', `/panel/api/images/${image.id}`, { alt: alt.value.trim() || null });
    });

    return el('li', { class: 'img-item' }, [
      el('img', { src: image.url, alt: image.alt ?? '', loading: 'lazy' }),
      alt,
      remove,
    ]);
  }

  async function upload(files) {
    if (files.length === 0) return;

    // No record yet: hold the bytes rather than refusing the pick. A failed or
    // missing upload must never be what stops an event being created.
    if (!series) {
      queued = queued.concat(files);
      status.textContent = t.flyerQueued;
      status.hidden = false;
      return;
    }

    status.textContent = t.uploading;
    status.hidden = false;

    for (const file of files) {
      const response = await fetch(`/panel/api/series/${series}/images`, {
        method: 'PUT',
        headers: { 'Content-Type': file.type, ...csrfHeader },
        credentials: 'same-origin',
        body: file,
      });
      if (!response.ok) {
        status.textContent = t.uploadFailed;
        return;
      }
    }

    status.hidden = true;
    picker.value = '';
    await refresh();
  }

  async function request(method, url, body = null) {
    const response = await fetch(url, {
      method,
      headers: method === 'GET'
        ? {}
        : { ...csrfHeader, ...(body ? { 'Content-Type': 'application/json' } : {}) },
      credentials: 'same-origin',
      body: body ? JSON.stringify(body) : undefined,
    });
    if (response.status === 204) return null;
    return response.ok ? response.json() : null;
  }

  return {
    node: root,
    // Called once the draft exists. Deliberately not awaited by the caller:
    // sharp re-encoding a phone photo takes seconds, and the publisher is on
    // the next slide by then.
    setSeriesId(id) {
      if (!id || series === id) return;
      series = id;
      const pending = queued;
      queued = [];
      upload(pending);
    },
  };
}
